import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Disc3, Plus, Trash2, AlertTriangle, FolderInput, Upload, Disc, Search as SearchIcon,
  Wrench, DownloadCloud, Library as LibraryIcon, FileArchive, Zap, FolderPlus, Fingerprint, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { UploadZone } from "@/components/riptag/UploadZone";
import { MbMatchPanel } from "@/components/riptag/MbMatchPanel";
import { TrackTable } from "@/components/riptag/TrackTable";
import { MetaEditor } from "@/components/riptag/MetaEditor";
import { LibraryTab } from "@/components/riptag/LibraryTab";
import { AlbumStrip } from "@/components/riptag/AlbumStrip";
import { MetadataTools } from "@/components/riptag/MetadataTools";
import { DownloadManager } from "@/components/riptag/DownloadManager";
import { downloadBlob } from "@/components/riptag/util";
import { appPrompt, appConfirm } from "@/components/AppDialogHost";

export default function RipTagStudio() {
  const [tab, setTab] = useState("sessions");
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [tracks, setTracks] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [albums, setAlbums] = useState([]);
  const [albumMeta, setAlbumMeta] = useState({ unassigned: 0, total_tracks: 0 });
  const [albumFilter, setAlbumFilter] = useState("all");
  const [checked, setChecked] = useState([]);
  const [toolsSignal, setToolsSignal] = useState(0);
  const [dmSignal, setDmSignal] = useState(0);
  const [dmPrefill, setDmPrefill] = useState(null);
  const uploadRef = useRef(null);
  const albumRef = useRef(null);
  const mbRef = useRef(null);
  const toolsRef = useRef(null);
  const dmRef = useRef(null);
  const footerRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const r = await api.get("/rip/sessions");
      setSessions(r.data.sessions || []);
      if (!activeId && r.data.sessions?.length) setActiveId(r.data.sessions[0].session_id);
    } catch { /* first load */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTracks = useCallback(async (sid) => {
    if (!sid) { setTracks([]); setAlbums([]); return; }
    try {
      const a = await api.get(`/rip/sessions/${sid}/albums`); // also auto-groups
      setAlbums(a.data.albums || []);
      setAlbumMeta({ unassigned: a.data.unassigned || 0, total_tracks: a.data.total_tracks || 0 });
      const r = await api.get(`/rip/sessions/${sid}/tracks`);
      setTracks(r.data.tracks || []);
    } catch { /* session gone */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadTracks(activeId); setSelectedId(""); setAlbumFilter("all"); setChecked([]); }, [activeId, loadTracks]);

  const newSession = async () => {
    const name = await appPrompt("Session name (e.g. album or artist you're importing):", "");
    if (name === null) return;
    const r = await api.post("/rip/sessions", { name });
    toast.success(`Session "${r.data.name}" created`);
    await loadSessions();
    setActiveId(r.data.session_id);
  };

  const [autoTag, setAutoTag] = useState(false);
  useEffect(() => {
    api.get("/rip/identify/settings").then((r) => setAutoTag(!!r.data.auto_tag)).catch(() => {});
  }, []);
  const toggleAutoTag = async (e) => {
    const v = e.target.checked;
    setAutoTag(v);
    try {
      await api.post("/rip/identify/settings", { auto_tag: v });
      toast.success(v ? "Strong fingerprint matches (95%+) will be auto-tagged" : "Auto-tagging off — matches shown as suggestions only");
    } catch {
      setAutoTag(!v);
    }
  };

  const [bulkIdent, setBulkIdent] = useState(null);
  const identifyAll = async () => {
    try {
      const r = await api.post(`/rip/sessions/${activeId}/identify-bulk`, { scope: "untitled" });
      setBulkIdent({ progress: 0, total: r.data.total });
      for (;;) {
        await new Promise((res) => setTimeout(res, 2500));
        const { data } = await api.get(`/rip/identify/jobs/${r.data.job_id}`);
        setBulkIdent({ progress: data.progress, total: data.total });
        if (data.status === "done") {
          const s = data.summary || {};
          toast.success(`Identified ${data.total} track${data.total === 1 ? "" : "s"} — ${s.auto_applied || 0} auto-tagged, ${s.suggested || 0} suggestions, ${s.unidentified || 0} unknown`);
          break;
        }
        if (data.status === "error") { toast.error(data.error || "Bulk identify failed"); break; }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk identify failed");
    }
    setBulkIdent(null);
    loadTracks(activeId);
  };

  const quickSession = async (suggested) => {
    const name = (suggested || "").trim() ||
      `Quick Upload — ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    const r = await api.post("/rip/sessions", { name });
    toast.success(`Session "${r.data.name}" created`);
    return r.data.session_id;
  };

  const deleteSession = async () => {
    if (!activeId || !await appConfirm("Delete this session and its non-library tracks?")) return;
    await api.delete(`/rip/sessions/${activeId}`);
    toast.success("Session deleted");
    setActiveId("");
    loadSessions();
  };

  const exportFile = async (kind, scope) => {
    try {
      const r = await api.get(`/rip/export/${kind}`, {
        params: scope === "library" ? { scope: "library" } : { session_id: activeId },
        responseType: "blob",
      });
      downloadBlob(r.data, kind === "csv" ? "broadcastflow_library.csv" : "broadcastflow_library.m3u8");
      toast.success(`${kind.toUpperCase()} exported — import it into SAM Broadcaster`);
    } catch { toast.error("Export failed"); }
  };

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const ripCdGuide = () =>
    appConfirm(
      "Browsers can't access CD drives directly, so ripping happens on your computer with a companion tool:\n\n" +
      "1. Rip your CD with Exact Audio Copy (free), CyberLink Power2Go, iTunes or Windows Media Player — choose MP3, FLAC or WAV.\n" +
      "2. Drag the ripped files into Upload Music here.\n" +
      "3. BroadcastFlow groups them into albums, identifies them with MusicBrainz and tags everything for broadcast.",
      { title: "Rip an Audio CD — desktop companion workflow", confirmText: "Got it" });

  const addAlbumToLibrary = async () => {
    const r = await api.post(`/rip/albums/${selectedAlbum.album_id}/library`);
    toast.success(`${r.data.added} tracks added to the Music Library${r.data.skipped_duplicates ? ` · ${r.data.skipped_duplicates} flagged duplicates skipped` : ""}`);
    loadTracks(activeId);
  };

  const downloadAlbum = () => {
    setDmPrefill({ albumId: selectedAlbum.album_id, n: Date.now() });
    setTimeout(() => scrollTo(dmRef), 100);
  };

  const NAV = [
    { id: "upload", label: "Upload Music", icon: Upload, go: () => scrollTo(uploadRef) },
    { id: "albums", label: "Album Manager", icon: Disc3, go: () => scrollTo(albumRef) },
    { id: "ripcd", label: "Rip Audio CD", icon: Disc, go: ripCdGuide },
    { id: "identify", label: "Identify Album", icon: SearchIcon, go: () => scrollTo(mbRef) },
    { id: "cleaner", label: "Metadata Cleaner", icon: Wrench, go: () => { setToolsSignal(Date.now()); setTimeout(() => scrollTo(toolsRef), 100); } },
    { id: "downloads", label: "Download Manager", icon: DownloadCloud, go: () => { setDmSignal(Date.now()); setTimeout(() => scrollTo(dmRef), 100); } },
    { id: "export", label: "Export to Library", icon: LibraryIcon, go: () => scrollTo(footerRef) },
  ];

  const selected = tracks.find((t) => t.track_id === selectedId) || null;
  const selectedAlbum = albums.find((a) => a.album_id === albumFilter) || null;
  const visibleTracks =
    albumFilter === "all" ? tracks
      : albumFilter === "unassigned" ? tracks.filter((t) => !t.album_id)
      : tracks.filter((t) => t.album_id === albumFilter);
  const dupCount = tracks.filter((t) => t.dup_status === "flagged").length;
  const readyCount = tracks.filter((t) => !t.in_library && t.dup_status !== "flagged").length;

  const createAlbum = async () => {
    const title = await appPrompt("New album title:", "");
    if (!title) return;
    const artist = await appPrompt("Album artist (leave empty or 'Various Artists' for compilations):", "");
    if (artist === null) return;
    const r = await api.post(`/rip/sessions/${activeId}/albums`, { title, artist: artist || "" });
    toast.success(`Album "${r.data.title}" created — select tracks and move them in`);
    await loadTracks(activeId);
    setAlbumFilter(r.data.album_id);
  };

  const editAlbum = async (a) => {
    const title = await appPrompt("Album title:", a.title);
    if (title === null) return;
    const artist = await appPrompt("Album artist:", a.artist || "");
    if (artist === null) return;
    const year = await appPrompt("Release year:", a.year || "");
    if (year === null) return;
    await api.put(`/rip/albums/${a.album_id}`, { title, artist, year, propagate: true });
    toast.success("Album updated — changes applied to its tracks");
    loadTracks(activeId);
  };

  const deleteAlbum = async (a) => {
    if (!await appConfirm(`Ungroup "${a.title}"? Its tracks stay in the session as Unassigned — nothing is deleted.`)) return;
    await api.delete(`/rip/albums/${a.album_id}`);
    toast.success("Album ungrouped");
    setAlbumFilter("all");
    loadTracks(activeId);
  };

  const moveChecked = async (albumId) => {
    await api.post("/rip/tracks/assign-album", { track_ids: checked, album_id: albumId });
    toast.success(`${checked.length} track${checked.length > 1 ? "s" : ""} moved`);
    setChecked([]);
    loadTracks(activeId);
  };

  const bulkEdit = async (field, label) => {
    const v = await appPrompt(`Set ${label} for ${checked.length} selected tracks:`, "");
    if (v === null) return;
    await api.post("/rip/tracks/bulk-edit", { track_ids: checked, [field]: v });
    toast.success(`${label} updated on ${checked.length} tracks`);
    setChecked([]);
    loadTracks(activeId);
  };

  return (
    <div className="space-y-6 px-3 pt-3 pb-6 md:p-0" data-testid="rip-tag-studio">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Disc3 className="h-6 w-6 text-brand-amber" /> Rip &amp; Tag Studio
          </h1>
          <p className="text-sm text-ink-secondary mt-1">
            Import ripped CDs or audio files, identify them with MusicBrainz, tag, and build your station library.
          </p>
        </div>
        <button onClick={newSession} data-testid="rip-new-session-btn"
          className="inline-flex items-center gap-2 rounded-full bg-brand-amber text-black px-4 py-2 text-sm font-bold hover:opacity-90 transition">
          <Plus className="h-4 w-4" /> New Rip Session
        </button>
      </div>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-200/90 flex items-start gap-2" data-testid="rip-rights-disclaimer">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          Importing audio here does <strong>not</strong> grant broadcast or public-performance rights.
          Airing this material requires your own PRO / performance-rights licensing, independent of how the file was obtained.
        </span>
      </div>

      <div className="flex gap-2">
        {[["sessions", "Rip Sessions"], ["library", "Music Library"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`rip-tab-${id}`}
            className={`rounded-full px-4 py-1.5 text-xs font-mono uppercase tracking-wider border transition ${
              tab === id ? "bg-brand-amber text-black border-brand-amber font-bold" : "border-line text-ink-secondary hover:text-ink-primary"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "library" ? (
        <LibraryTab onExport={exportFile} />
      ) : (
        <>
          <div className="flex items-center justify-end gap-4">
            {activeId && (
              <button onClick={identifyAll} disabled={!!bulkIdent} data-testid="rip-bulk-identify-btn"
                title="Fingerprints every track in this session that has no artist or still carries its filename / Part N title"
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-cyan/50 text-brand-cyan px-3 py-1.5 text-[11px] font-bold hover:bg-brand-cyan/10 transition disabled:opacity-50">
                {bulkIdent ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Fingerprint className="h-3.5 w-3.5" />}
                {bulkIdent ? `Identifying ${bulkIdent.progress}/${bulkIdent.total}…` : "Identify all untitled tracks"}
              </button>
            )}
            <label className="flex items-center gap-2 text-[11px] text-ink-secondary cursor-pointer"
              title="When ON, fingerprint matches at 95%+ with a verified duration are applied automatically. Original metadata is always kept for undo.">
              <input type="checkbox" checked={autoTag} onChange={toggleAutoTag}
                className="accent-amber-500" data-testid="rip-auto-tag-toggle" />
              Auto-tag strong song matches (95%+)
            </label>
          </div>

          {sessions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <select value={activeId} onChange={(e) => setActiveId(e.target.value)} data-testid="rip-session-select"
                className="rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm w-full sm:w-auto sm:min-w-[220px] max-w-full focus:border-brand-amber focus:outline-none">
                {!activeId && <option value="">Select a session…</option>}
                {sessions.map((s) => (
                  <option key={s.session_id} value={s.session_id}>{s.name} ({s.track_count})</option>
                ))}
              </select>
              {activeId && (
                <button onClick={deleteSession} data-testid="rip-delete-session-btn"
                  className="rounded-md border border-line p-2 text-ink-muted hover:text-red-400 transition" title="Delete session">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {!activeId && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="rip-get-started">
              <div className="lg:col-span-3 rounded-xl border border-line bg-bg-surface p-5" data-testid="rip-quick-upload-card">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="h-4 w-4 text-brand-amber" />
                  <h3 className="font-display font-bold text-sm uppercase tracking-wider">Quick Upload</h3>
                </div>
                <p className="text-xs text-ink-secondary mb-3">
                  Drag in MP3, WAV, FLAC and other audio — single tracks, multiple files or a complete album folder.
                  A rip session is created for you automatically, no extra steps.
                </p>
                <UploadZone
                  sessionId=""
                  onNeedSession={quickSession}
                  onUploaded={async (sid) => { await loadSessions(); if (sid) setActiveId(sid); }}
                />
              </div>
              <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-5 flex flex-col" data-testid="rip-new-session-card">
                <div className="flex items-center gap-2 mb-1">
                  <FolderPlus className="h-4 w-4 text-brand-cyan" />
                  <h3 className="font-display font-bold text-sm uppercase tracking-wider">New Rip Session</h3>
                </div>
                <p className="text-xs text-ink-secondary">
                  Start a named workspace for album identification, metadata editing and building your station's music library.
                </p>
                <ul className="text-xs text-ink-muted mt-3 space-y-1.5 font-mono">
                  <li>· Group uploads into albums</li>
                  <li>· Identify &amp; tag with MusicBrainz</li>
                  <li>· Clean metadata, flag duplicates</li>
                  <li>· Export to your Music Library &amp; playout</li>
                </ul>
                <div className="mt-auto pt-4">
                  <button onClick={newSession} data-testid="rip-create-session-card-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-cyan/60 text-brand-cyan px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-brand-cyan/10 transition">
                    <Plus className="h-4 w-4" /> Create Rip Session
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeId && (
            <>
              <div className="flex flex-wrap gap-1.5" data-testid="rip-top-nav">
                {NAV.map((n) => (
                  <button key={n.id} onClick={n.go} data-testid={`rip-nav-${n.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg-surface px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider text-ink-secondary hover:text-brand-amber hover:border-brand-amber/60 transition">
                    <n.icon className="h-3 w-3" /> {n.label}
                  </button>
                ))}
              </div>

              <div ref={uploadRef} className="scroll-mt-4">
                <UploadZone sessionId={activeId} onUploaded={() => loadTracks(activeId)} />
              </div>

              <div ref={albumRef} className="scroll-mt-4">
                <AlbumStrip albums={albums} unassigned={albumMeta.unassigned} totalTracks={albumMeta.total_tracks}
                  filter={albumFilter} onFilter={(f) => { setAlbumFilter(f); setChecked([]); setSelectedId(""); }}
                  onCreate={createAlbum} onEdit={editAlbum} onDelete={deleteAlbum} />
              </div>

              <div ref={mbRef} className="scroll-mt-4">
                <MbMatchPanel sessionId={activeId} tracks={visibleTracks} album={selectedAlbum} onApplied={() => loadTracks(activeId)} />
              </div>

              <div ref={toolsRef} className="scroll-mt-4">
                <MetadataTools sessionId={activeId} album={selectedAlbum} track={selected} onChanged={() => loadTracks(activeId)} openSignal={toolsSignal} />
              </div>

              <div ref={dmRef} className="scroll-mt-4">
                <DownloadManager sessionId={activeId} albums={albums} onChanged={() => loadTracks(activeId)}
                  openSignal={dmSignal} prefillAlbum={dmPrefill} />
              </div>

              {dupCount > 0 && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-xs text-amber-200" data-testid="rip-dup-warning">
                  ⚠ {dupCount} track{dupCount > 1 ? "s look" : " looks"} like a possible duplicate — review in the editor before adding to the library. Clean/explicit variants can trigger this; they are never auto-merged.
                </div>
              )}

              {checked.length > 0 && (
                <div className="rounded-lg border border-brand-amber/50 bg-brand-amber/10 px-4 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="rip-selection-bar">
                  <span className="font-bold text-brand-amber">{checked.length} selected</span>
                  <span className="inline-flex items-center gap-1 text-ink-secondary"><FolderInput className="h-3.5 w-3.5" /> Move to:</span>
                  <select defaultValue="" onChange={(e) => { if (e.target.value !== "") moveChecked(e.target.value === "__none" ? "" : e.target.value); }}
                    data-testid="rip-move-select"
                    className="rounded-md border border-line bg-bg-elevated px-2 py-1 text-xs focus:border-brand-amber focus:outline-none">
                    <option value="" disabled>Choose album…</option>
                    {albums.map((a) => <option key={a.album_id} value={a.album_id}>{a.title}</option>)}
                    <option value="__none">— Unassigned —</option>
                  </select>
                  <span className="text-ink-muted">·</span>
                  <button onClick={() => bulkEdit("genre", "genre")} data-testid="rip-bulk-genre" className="underline text-ink-secondary hover:text-ink-primary">Set genre</button>
                  <button onClick={() => bulkEdit("year", "year")} data-testid="rip-bulk-year" className="underline text-ink-secondary hover:text-ink-primary">Set year</button>
                  <button onClick={() => bulkEdit("rotation", "rotation (heavy / medium / light / unassigned)")} data-testid="rip-bulk-rotation" className="underline text-ink-secondary hover:text-ink-primary">Set rotation</button>
                  <button onClick={() => setChecked([])} data-testid="rip-clear-selection" className="ml-auto text-ink-muted hover:text-ink-primary">Clear selection</button>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <TrackTable tracks={visibleTracks} selectedId={selectedId} onSelect={setSelectedId}
                    checked={checked} onCheck={setChecked} />
                </div>
                <MetaEditor track={selected} onChanged={() => loadTracks(activeId)} />
              </div>

              <div ref={footerRef} className="rounded-xl border border-line bg-bg-surface px-4 py-3 flex flex-wrap items-center justify-between gap-3 scroll-mt-4" data-testid="rip-footer-bar">
                <div className="text-xs font-mono text-ink-secondary">
                  {tracks.length} tracks · {readyCount} ready for library · {tracks.filter((t) => t.in_library).length} in library
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedAlbum && (
                    <button onClick={downloadAlbum} data-testid="rip-download-album-btn"
                      className="inline-flex items-center gap-1.5 rounded-full border border-brand-cyan/60 text-brand-cyan px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-brand-cyan/10 transition">
                      <FileArchive className="h-3.5 w-3.5" /> Download Album
                    </button>
                  )}
                  <button onClick={() => exportFile("m3u8", "session")} data-testid="rip-export-m3u8-btn"
                    className="rounded-full border border-line px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-ink-secondary hover:text-ink-primary transition">
                    Export M3U8
                  </button>
                  <button onClick={() => exportFile("csv", "session")} data-testid="rip-export-csv-btn"
                    className="rounded-full border border-line px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-ink-secondary hover:text-ink-primary transition">
                    Export CSV
                  </button>
                  {selectedAlbum ? (
                    <button data-testid="rip-add-album-library-btn" onClick={addAlbumToLibrary}
                      className="rounded-full bg-brand-cyan/90 text-black px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:opacity-90 transition">
                      Add "{selectedAlbum.title.length > 22 ? selectedAlbum.title.slice(0, 22) + "…" : selectedAlbum.title}" to Library
                    </button>
                  ) : (
                    <button data-testid="rip-add-all-library-btn"
                      onClick={async () => {
                        const r = await api.post(`/rip/sessions/${activeId}/library-all`);
                        toast.success(`${r.data.added} tracks added to Music Library`);
                        loadTracks(activeId);
                      }}
                      className="rounded-full bg-brand-cyan/90 text-black px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:opacity-90 transition">
                      Add to Music Library
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
